import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useGetAllOrderLinesCached, useGetProductsCached } from '../hooks/cachedQueries'
import {
  useCommitPick,
  useProcessToteScan,
  useCheckInTote,
  useUpdateOrderLine,
  useFlushOrderLineUpdates,
  useUndoPick,
  useAssignPickBins,
  usePostBinLabel,
} from '../hooks/backend/scanPage'
import { playSound } from './ui/scanSounds'
import '../station.css'
import FlavorPickPanel, {
  linesNeedingSku,
  openOrderLines,
  isPackableStatus,
  unprintedOrderIds,
  heldOrderIds,
  toteName,
  lowestBin,
  ordersNeedingBins,
  loadPickMode,
  savePickMode,
  type PickMode,
} from './ui/FlavorPickPanel'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Badge } from '../lib/shadcn/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import { cn } from '../lib/shadcn/utils'
import { RefreshCw, Download, Filter, CheckCheck, PackagePlus, X, Keyboard, Undo2, Plus, Minus } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

// Deferred Zoho sync payload returned by updateOrderLine when defer_sync is set.
interface SyncPayload {
  order_id: string
  salesorder_id: string
  line_id: string
  item_id: string | null
  sku: string
  item_name: string
  quantity: number
  rate: number | null
  action: 'add' | 'update'
}

interface OrderLine {
  id: number
  order_id: string
  item_name: string
  item_sku: string
  quantity_ordered: number
  quantity_picked: number
  quantity_printed?: number
  ready_to_pick_at?: string | null
  customer?: string
  /** customers.contact_number, the customer part of tote QR codes. */
  contact_number?: string
  tote_name?: string | null
  order_status?: string
  /** Web store / Faire order under a shared customer: its own tote. */
  catch_all?: boolean
  /** Zoho customer id (customers.customer_id), for printing a tote label. */
  customer_id?: string
  /** Numbered bin for this pick run (orders.pick_bin). */
  pick_bin?: number | null
}

// The tote (by customer) or order that bags are being packed into in flavor mode.
interface PackTarget {
  kind: 'tote' | 'order'
  /** contact_number (upper case) for a tote, order number for an order. */
  key: string
  name: string
  /** Set when the tote was picked from a flavor lookup: it lets go once that flavor is in. */
  flavor?: string
}

// A bag that isn't on the selected tote's order (or more than it ordered),
// waiting for "Add to order" at the station.
interface AddPrompt {
  sku: string
  name: string
  /** salesorder_number the bags get added to */
  orderId: string
  /** The tote's display name, e.g. "Smith's Market" */
  who: string
  /** How many bags: one per scan of this flavor, or +/- */
  qty: number
  /** true when the order already has this flavor (all packed) */
  onOrder: boolean
}

// What commitPick reports back, so a pick can be undone exactly.
interface CommitResult {
  stock_taken?: number
  batch_oz_taken?: number
  invoice_sent?: boolean
  previous_status?: string | null
  order_completed?: boolean
}

// One order line a pick touched.
interface UndoPart {
  line_id: number
  order_id: string
  sku: string
  units: number
  /** Settles to null when the pick failed (it was already rolled back). */
  result: Promise<CommitResult | null>
}

// One scan or Pick All, undone together with "Undo last scan".
interface UndoEntry {
  key: number
  /** e.g. "1 Jalapeño into Smith's tote (SO-00123)" */
  label: string
  parts: UndoPart[]
  /** The bags were added to the order at the station (Add to order). */
  added?: boolean
}

// How many scans back "Undo last scan" can go.
const UNDO_LIMIT = 25

interface PickListRow {
  id: number
  order_id: string
  item_sku: string
  item_name: string
  quantity_ordered: number
  quantity_picked: number
  quantity_remaining: number
  status: 'Complete' | 'In Progress' | 'Not Started'
}

// ─── Transformers (mirror classic app logic) ──────────────────────────────────

// An order is "done" when its stored status is complete OR every line is fully
// picked (total picked >= total ordered). The latter guards against orders that
// were fully picked but never got their status flipped to complete — those
// should not inflate the pick counts.
function getDoneOrderIds(orders: OrderLine[]): Set<string> {
  const totals = new Map<string, { ordered: number; picked: number; complete: boolean }>()
  for (const o of orders) {
    const key = String(o.order_id)
    const entry = totals.get(key) ?? { ordered: 0, picked: 0, complete: false }
    entry.ordered += Number(o.quantity_ordered ?? 0)
    entry.picked += Number(o.quantity_picked ?? 0)
    if (!isPackableStatus(o.order_status)) entry.complete = true
    totals.set(key, entry)
  }
  const done = new Set<string>()
  for (const [key, t] of totals) {
    if (t.complete || (t.ordered > 0 && t.picked >= t.ordered)) done.add(key)
  }
  return done
}

function computeStats(orders: OrderLine[]) {
  // Only count orders that aren't done (complete status or fully picked)
  const done = getDoneOrderIds(orders)
  const incomplete = orders.filter(o => !done.has(String(o.order_id)))
  const uniqueOrders = new Set(incomplete.map(o => String(o.order_id))).size
  const totalItems = incomplete.reduce(
    (sum, o) => sum + Number(o.quantity_ordered ?? 0),
    0
  )
  const pickedItems = incomplete.reduce(
    (sum, o) => sum + Number(o.quantity_picked ?? 0),
    0
  )
  const remainingItems = Math.max(0, totalItems - pickedItems)
  return { totalOrders: uniqueOrders, totalItems, pickedItems, remainingItems }
}

function computePickList(
  orders: OrderLine[],
  currentOrderId: string
): PickListRow[] {
  // Only pick from orders that aren't done (complete status or fully picked).
  // When an order is explicitly selected, always show its lines so a picker can
  // review/re-pick it even if the aggregate looks complete.
  const done = getDoneOrderIds(orders)
  const pickable = currentOrderId
    ? orders
    : orders.filter(o => !done.has(String(o.order_id)))
  const filtered = currentOrderId
    ? pickable.filter(o => String(o.order_id) === String(currentOrderId))
    : pickable

  const map = new Map<string, PickListRow>()
  for (const line of filtered) {
    const key = `${line.item_sku}__${line.item_name}`
    const existing = map.get(key) ?? {
      id: line.id,
      order_id: line.order_id,
      item_sku: line.item_sku,
      item_name: line.item_name,
      quantity_ordered: 0,
      quantity_picked: 0,
      quantity_remaining: 0,
      status: 'In Progress' as const,
    }
    existing.quantity_ordered += Number(line.quantity_ordered ?? 0)
    existing.quantity_picked += Number(line.quantity_picked ?? 0)
    map.set(key, existing)
  }

  return Array.from(map.values())
    .map(row => ({
      ...row,
      quantity_remaining: Math.max(0, row.quantity_ordered - row.quantity_picked),
      status: (row.quantity_picked >= row.quantity_ordered
        ? 'Complete'
        : row.quantity_picked === 0
          ? 'Not Started'
          : 'In Progress') as 'Complete' | 'In Progress' | 'Not Started',
    }))
    // Keep completed items at the bottom of the pick list
    .sort((a, b) => {
      const aDone = a.quantity_remaining === 0 ? 1 : 0
      const bDone = b.quantity_remaining === 0 ? 1 : 0
      return aDone - bDone
    })
}

// ─── Page ─────────────────────────────────────────────────────────────────────

// station: the one-flow scan layout (no By order / By flavor switch, bigger
// controls, one slim top bar). Used by the full-screen Scan Station (/station)
// and the Hub's Scan tab (/scan, title "Scan"). Without it, the older layout
// with the switch renders.
export default function ScanPage({
  station = false,
  title = 'Scan Station',
}: { station?: boolean; title?: string } = {}) {
  // Backend hooks
  const getAllOrderLines = useGetAllOrderLinesCached()
  const commitPick = useCommitPick()
  const processToteScan = useProcessToteScan()
  const checkInTote = useCheckInTote()
  const updateOrderLine = useUpdateOrderLine()
  const flushOrderLineUpdates = useFlushOrderLineUpdates()
  const undoPick = useUndoPick()
  const assignPickBins = useAssignPickBins()
  const postBinLabel = usePostBinLabel()
  const [printingLabel, setPrintingLabel] = useState(false)
  // The product catalog, so a bag of a flavor no open order has can still be
  // added to the selected tote's order.
  const getProducts = useGetProductsCached()

  // UI state
  const [searchParams] = useSearchParams()
  const hasAppliedUrlOrder = useRef(false)

  const [currentOrderId, setCurrentOrderId] = useState('')
  const [currentCustomerId, setCurrentCustomerId] = useState('')
  const [toteCheckInMode, setToteCheckInMode] = useState(false)
  const [updateMode, setUpdateMode] = useState(false)
  const [lastScannedSku, setLastScannedSku] = useState('')
  const [lastScannedOrderId, setLastScannedOrderId] = useState('')
  const [scanStatus, setScanStatus] = useState(station ? 'Scan a tote or a bag to start' : 'Ready to scan...')
  const [scanInputValue, setScanInputValue] = useState('')
  const [filterValue, setFilterValue] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [pickingRows, setPickingRows] = useState<Set<string>>(new Set())
  // By order (scan an order, then its products) or by flavor (scan a bag, then
  // the tote it goes in). Remembered per device.
  const [savedPickMode, setPickModeState] = useState<PickMode>(() => loadPickMode())
  // The Scan Station has no By order / By flavor switch: it always works the
  // flavor way, where scanning a tote (or order label) shows that tote and a
  // bag scan with no tote shows every tote that needs it.
  const pickMode: PickMode = station ? 'flavor' : savedPickMode
  const [activeSku, setActiveSku] = useState('')
  const [flavorPickingIds, setFlavorPickingIds] = useState<Set<number>>(new Set())
  const [finishedHere, setFinishedHere] = useState<Set<string>>(new Set())
  const [packTarget, setPackTarget] = useState<PackTarget | null>(null)
  // The flavor looked up by scanning a bag with no tote (or tapping it in
  // Flavors to pick). Totes chosen for it let go once they have it all.
  const lookupSkuRef = useRef('')
  const packTargetRef = useRef(packTarget)
  packTargetRef.current = packTarget
  // On iPad the scanner types like a keyboard, so the on-screen keyboard is
  // kept hidden unless someone wants to type a code by hand.
  const [showKeyboard, setShowKeyboard] = useState(false)
  const scanInputRef = useRef<HTMLInputElement>(null)
  // Timestamp of the last scan, used to avoid refreshing mid-pick.
  const lastActivityRef = useRef(0)
  // Deferred Zoho sync payloads accumulated during UPDATEORDER mode, keyed by
  // line_id. Flushed as one batch when the operator exits the process.
  const pendingSyncRef = useRef<Map<string, SyncPayload>>(new Map())
  // Picks made on this screen, newest last, for "Undo last scan".
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([])
  const [undoing, setUndoing] = useState(false)
  const undoKeyRef = useRef(0)
  // Station "Add to order" prompt for a bag the tote's order doesn't have.
  const [addPrompt, setAddPrompt] = useState<AddPrompt | null>(null)
  const [adding, setAdding] = useState(false)

  // Derived data.
  // We keep a local, mutable copy of the order lines so scans can be applied
  // optimistically (instant UI) without waiting on a network round-trip or
  // re-fetching the entire table after every pick.
  const [orders, setOrders] = useState<OrderLine[]>([])
  useEffect(() => {
    const data = getAllOrderLines.data as (OrderLine & { product_name?: string | null })[] | undefined
    // Show each bag under its product's current name, so a line saved under
    // an old name lands with the rest of that flavor (#229).
    if (data) setOrders(data.map(l => (l.product_name ? { ...l, item_name: l.product_name } : l)))
  }, [getAllOrderLines.data])

  // Numbered bins (#197): orders that show up for picking without a bin get
  // the lowest free numbers, then the lines are reloaded so every screen
  // (Station and Scan tab) shows the same numbers. Each order is only asked
  // for once per visit, so a failure can't loop.
  const binsAskedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (pickMode !== 'flavor' || getAllOrderLines.loading || !getAllOrderLines.data) return
    const ids = ordersNeedingBins(orders).filter(id => !binsAskedRef.current.has(id))
    if (ids.length === 0) return
    for (const id of ids) binsAskedRef.current.add(id)
    void (async () => {
      try {
        const given = (await assignPickBins.trigger({ order_ids: ids })) as
          | Array<{ order_id: string; pick_bin: number }>
          | null
        const bins = new Map((given ?? []).map(g => [String(g.order_id), Number(g.pick_bin)]))
        if (bins.size > 0) {
          setOrders(prev =>
            prev.map(o =>
              bins.has(String(o.order_id)) ? { ...o, pick_bin: bins.get(String(o.order_id))! } : o,
            ),
          )
        }
        // Another screen may have numbered some of them first: reload to match.
        void getAllOrderLines.trigger(undefined, { skipCache: true })
      } catch {
        // No bin numbers this time; totes still show by name.
      }
    })()
  }, [orders, pickMode, getAllOrderLines.loading, getAllOrderLines.data, assignPickBins, getAllOrderLines])

  // Patch a single line's picked quantity in local state.
  const patchPicked = useCallback((id: number, quantity_picked: number) => {
    setOrders(prev =>
      prev.map(o => (o.id === id ? { ...o, quantity_picked } : o))
    )
  }, [])

  // Persist a pick with commitPick and remember it so it can be undone.
  // Returns the commitPick promise (rejects on failure, like trigger does).
  const commitAndRecord = useCallback(
    (
      args: {
        line_id: number
        quantity_picked: number
        sku: string
        decrement_quantity: number
        order_id: string
        item_complete: boolean
        order_complete: boolean
      },
      label: string,
      units: number,
      group?: UndoPart[],
    ) => {
      const promise = commitPick.trigger(args) as Promise<unknown>
      const part: UndoPart = {
        line_id: args.line_id,
        order_id: args.order_id,
        sku: args.sku,
        units,
        result: promise.then(r => (r ?? {}) as CommitResult).catch(() => null),
      }
      if (group) group.push(part)
      else {
        const entry: UndoEntry = { key: ++undoKeyRef.current, label, parts: [part] }
        setUndoStack(prev => [...prev, entry].slice(-UNDO_LIMIT))
      }
      return promise
    },
    [commitPick],
  )

  // Merge an added/incremented line (from updateOrderLine) into local state so
  // UPDATEORDER scans update the table instantly without a full refetch.
  const applyUpdatedLine = useCallback(
    (
      line: {
        id: number | string
        item_name: string
        item_sku: string
        quantity_ordered: number
        quantity_picked: number
        quantity_printed?: number
      },
      orderId: string
    ) => {
      setOrders(prev => {
        const idx = prev.findIndex(
          o => String(o.order_id) === String(orderId) && o.item_sku === line.item_sku
        )
        if (idx >= 0) {
          const next = [...prev]
          const existing = next[idx]!
          next[idx] = {
            ...existing,
            quantity_ordered: Number(line.quantity_ordered),
            quantity_picked: Number(line.quantity_picked),
          }
          return next
        }
        // New line: inherit customer/order_status from a sibling line on the
        // same order so filtering/stats stay consistent.
        const sibling = prev.find(o => String(o.order_id) === String(orderId))
        const newRow: OrderLine = {
          id: line.id as unknown as number,
          order_id: orderId,
          item_name: line.item_name,
          item_sku: line.item_sku,
          quantity_ordered: Number(line.quantity_ordered),
          quantity_picked: Number(line.quantity_picked),
          ...(sibling?.customer !== undefined ? { customer: sibling.customer } : {}),
          ...(sibling?.order_status !== undefined
            ? { order_status: sibling.order_status }
            : {}),
          // Tote fields, so a line added at the station shows in its tote.
          ...(line.quantity_printed !== undefined
            ? { quantity_printed: Number(line.quantity_printed) }
            : {}),
          ...(sibling?.contact_number !== undefined ? { contact_number: sibling.contact_number } : {}),
          ...(sibling?.tote_name !== undefined ? { tote_name: sibling.tote_name } : {}),
          ...(sibling?.ready_to_pick_at !== undefined ? { ready_to_pick_at: sibling.ready_to_pick_at } : {}),
          ...(sibling?.catch_all !== undefined ? { catch_all: sibling.catch_all } : {}),
          ...(sibling?.customer_id !== undefined ? { customer_id: sibling.customer_id } : {}),
          ...(sibling?.pick_bin !== undefined ? { pick_bin: sibling.pick_bin } : {}),
        }
        return [...prev, newRow]
      })
    },
    []
  )

  // Queue a deferred sync payload, keyed by line_id. The latest scan carries the
  // final absolute quantity, so we overwrite — but keep action 'add' if the line
  // was created at any point during the session (Zoho doesn't have it yet).
  const queueSync = useCallback((payload: SyncPayload) => {
    const key = String(payload.line_id)
    const existing = pendingSyncRef.current.get(key)
    pendingSyncRef.current.set(key, {
      ...payload,
      action: existing?.action === 'add' ? 'add' : payload.action,
    })
  }, [])

  // Flush all queued syncs to Zoho in one batch. Called when exiting UPDATEORDER.
  const flushPendingSync = useCallback(() => {
    const updates = Array.from(pendingSyncRef.current.values())
    pendingSyncRef.current.clear()
    if (!updates.length) return
    setScanStatus(
      `⏳ Syncing ${updates.length} change${updates.length !== 1 ? 's' : ''} to Zoho…`,
    )
    void (async () => {
      try {
        const res = (await flushOrderLineUpdates.trigger({ updates })) as {
          pushed: number
          failed: Array<unknown>
        }
        if (res.failed?.length) {
          playSound('error')
          setScanStatus(
            `⚠ Synced ${res.pushed}, ${res.failed.length} failed to sync to Zoho`,
          )
        } else {
          playSound('success')
          setScanStatus(
            `✓ Synced ${res.pushed} change${res.pushed !== 1 ? 's' : ''} to Zoho`,
          )
        }
      } catch (error) {
        playSound('error')
        setScanStatus(
          `⚠ Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        )
      }
    })()
  }, [flushOrderLineUpdates])

  // Safety net: if the operator navigates away while changes are still queued,
  // flush them on unmount. A ref keeps the effect from re-running each render.
  const flushTriggerRef = useRef(flushOrderLineUpdates.trigger)
  flushTriggerRef.current = flushOrderLineUpdates.trigger
  useEffect(
    () => () => {
      const updates = Array.from(pendingSyncRef.current.values())
      pendingSyncRef.current.clear()
      if (updates.length) void flushTriggerRef.current({ updates })
    },
    [],
  )

  const stats = useMemo(() => computeStats(orders), [orders])

  // Products by SKU (name), for bags of flavors no open order has.
  const catalog = useMemo(() => {
    const map = new Map<string, { item_name: string }>()
    for (const p of (getProducts.data as { sku?: string; item_name?: string }[] | null) ?? []) {
      if (p.sku) map.set(String(p.sku), { item_name: String(p.item_name ?? p.sku) })
    }
    return map
  }, [getProducts.data])

  // Live snapshot of the most recently scanned item (reflects refreshed data)
  const lastScannedItem = useMemo<OrderLine | null>(() => {
    if (!lastScannedSku || !lastScannedOrderId) return null
    return (
      orders.find(
        r =>
          String(r.order_id) === String(lastScannedOrderId) &&
          r.item_sku === lastScannedSku
      ) ?? null
    )
  }, [orders, lastScannedSku, lastScannedOrderId])
  const pickListData = useMemo(
    () => computePickList(orders, currentOrderId),
    [orders, currentOrderId]
  )

  // Pick every remaining unit of a pick-list row's item. A row can represent
  // multiple underlying order lines (one item scanned across several open
  // orders), so each matching line is filled and persisted individually.
  const pickAllRow = useCallback(
    async (row: PickListRow) => {
      const key = `${row.item_sku}__${row.item_name}`
      const done = getDoneOrderIds(orders)
      const linesToPick = orders.filter(
        o =>
          o.item_sku === row.item_sku &&
          o.item_name === row.item_name &&
          (currentOrderId
            ? String(o.order_id) === String(currentOrderId)
            : !done.has(String(o.order_id))) &&
          Number(o.quantity_picked) < Number(o.quantity_ordered)
      )

      if (linesToPick.length === 0) {
        setScanStatus(`⚠ Item ${row.item_sku} already fully picked`)
        return
      }

      setPickingRows(prev => new Set(prev).add(key))

      // ── Optimistic update ── fill every matching line locally right away.
      const prevQuantities = new Map(linesToPick.map(l => [l.id, Number(l.quantity_picked)]))
      for (const line of linesToPick) {
        patchPicked(line.id, Number(line.quantity_ordered))
      }

      const affectedOrders = new Set(linesToPick.map(l => l.order_id))
      playSound('complete')
      setScanStatus(
        affectedOrders.size === 1
          ? `✅ Item ${row.item_sku} COMPLETE for order ${linesToPick[0]?.order_id ?? ''}`
          : `✅ Item ${row.item_sku} picked in full across ${affectedOrders.size} orders`
      )

      // ── Background persistence (one round-trip per underlying line) ──
      const parts: UndoPart[] = []
      try {
        for (const line of linesToPick) {
          const ordered = Number(line.quantity_ordered)
          const remaining = ordered - Number(prevQuantities.get(line.id) ?? 0)
          const siblingLines = orders.filter(o => o.order_id === line.order_id)
          const orderComplete = siblingLines.every(o =>
            o.id === line.id ? true : Number(o.quantity_picked) >= Number(o.quantity_ordered)
          )
          try {
            await commitAndRecord(
              {
                line_id: line.id,
                quantity_picked: ordered,
                sku: line.item_sku,
                decrement_quantity: remaining,
                order_id: line.order_id,
                item_complete: true,
                order_complete: orderComplete,
              },
              '',
              remaining,
              parts,
            )
          } catch (error) {
            patchPicked(line.id, prevQuantities.get(line.id) ?? 0)
            playSound('error')
            setScanStatus(
              `⚠ Pick All failed for order ${line.order_id}: ${error instanceof Error ? error.message : 'Unknown error'}`
            )
          }
        }
      } finally {
        if (parts.length) {
          const units = parts.reduce((sum, p) => sum + p.units, 0)
          const entry: UndoEntry = {
            key: ++undoKeyRef.current,
            label:
              affectedOrders.size === 1
                ? `Pick All: ${units} ${row.item_name} for ${linesToPick[0]?.order_id ?? ''}`
                : `Pick All: ${units} ${row.item_name} across ${affectedOrders.size} orders`,
            parts,
          }
          setUndoStack(prev => [...prev, entry].slice(-UNDO_LIMIT))
        }
        setPickingRows(prev => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
      }
    },
    [orders, currentOrderId, patchPicked, commitAndRecord]
  )

  // ─── Flavor-first picking ─────────────────────────────────────────────────

  const setPickMode = useCallback((mode: PickMode) => {
    setPickModeState(mode)
    savePickMode(mode)
    setActiveSku('')
    lookupSkuRef.current = ''
    setPackTarget(null)
    setCurrentOrderId('')
    setLastScannedSku('')
    setLastScannedOrderId('')
    setScanStatus(
      mode === 'flavor'
        ? 'Scan a bag of a flavor to see which totes need it'
        : 'Ready to scan...',
    )
    window.setTimeout(() => scanInputRef.current?.focus(), 0)
  }, [])

  // A flavor no ready tote still needs lets go by itself, so a finished
  // flavor doesn't sit on the pick screen until someone taps Clear.
  useEffect(() => {
    if (!activeSku || getAllOrderLines.loading) return
    if (linesNeedingSku(orders, activeSku).length > 0) return
    setActiveSku('')
    if (lookupSkuRef.current === activeSku) lookupSkuRef.current = ''
  }, [activeSku, orders, getAllOrderLines.loading])

  const selectFlavor = useCallback(
    (sku: string) => {
      setActiveSku(sku)
      lookupSkuRef.current = sku
      const needing = linesNeedingSku(orders, sku)
      const name = orders.find(o => o.item_sku === sku)?.item_name ?? sku
      if (needing.length === 0) {
        playSound('error')
        setScanStatus(`⚠ No open orders need ${name}`)
      } else {
        playSound('load_order')
        const bags = needing.reduce(
          (sum, l) => sum + Number(l.quantity_ordered) - Number(l.quantity_picked),
          0,
        )
        setScanStatus(
          `${name}: ${bags} bag${bags !== 1 ? 's' : ''} for ${needing.length} tote${needing.length !== 1 ? 's' : ''}. Scan a tote, then each bag as it goes in.`,
        )
      }
      scanInputRef.current?.focus()
    },
    [orders],
  )

  // Pack `units` bags of one order line (flavor mode). Same backend call as a
  // normal pick, so stock, invoice, order status and B2B stage all update.
  const packUnits = useCallback(
    (line: OrderLine, units: number, opts: { skipInvoice?: boolean } = {}) => {
      const ordered = Number(line.quantity_ordered)
      const prevQtyPicked = Number(line.quantity_picked)
      const add = Math.min(units, ordered - prevQtyPicked)
      if (add <= 0) return
      const newQtyPicked = prevQtyPicked + add
      const itemComplete = newQtyPicked >= ordered
      const orderComplete = orders
        .filter(o => o.order_id === line.order_id)
        .every(o =>
          o.id === line.id
            ? itemComplete
            : Number(o.quantity_picked) >= Number(o.quantity_ordered),
        )

      patchPicked(line.id, newQtyPicked)
      setLastScannedSku(line.item_sku)
      setLastScannedOrderId(line.order_id)
      setFlavorPickingIds(prev => new Set(prev).add(line.id))

      const otherTotes = linesNeedingSku(orders, line.item_sku).filter(
        l => l.id !== line.id,
      ).length
      const name = toteName(line)
      const who = name ? `${name} (${line.order_id})` : `Order ${line.order_id}`
      if (orderComplete) {
        setFinishedHere(prev => new Set(prev).add(String(line.order_id)))
        // A full tote lets go by itself, so the next scan starts fresh.
        setPackTarget(prev => {
          if (!prev) return prev
          const inTarget = (o: OrderLine) =>
            prev.kind === 'tote'
              ? String(o.contact_number ?? '').trim().toUpperCase() === prev.key
              : String(o.order_id) === prev.key
          if (!inTarget(line)) return prev
          const full = openOrderLines(orders)
            .filter(inTarget)
            .every(o =>
              o.id === line.id
                ? itemComplete
                : Number(o.quantity_picked) >= Number(o.quantity_ordered),
            )
          return full ? null : prev
        })
        playSound('complete')
        setScanStatus(
          `🎉 ${who} COMPLETE! Seal the tote and scan the next one.${
            itemComplete && otherTotes === 0 ? ` ${line.item_name} is done in every tote.` : ''
          }`,
        )
      } else if (itemComplete && otherTotes === 0) {
        lookupSkuRef.current = ''
        setPackTarget(prev => (prev?.flavor === line.item_sku ? null : prev))
        playSound('complete')
        setScanStatus(
          `✅ ${line.item_name} done in every tote. Scan the next flavor.`,
        )
      } else if (itemComplete) {
        // Picked for this flavor: the tote's done with it, so let go and
        // the next tote scan carries on with the flavor.
        const released = packTargetRef.current?.flavor === line.item_sku
        if (released) setPackTarget(null)
        playSound('complete')
        setScanStatus(
          `✅ ${who} has all its ${line.item_name}. ${otherTotes} more tote${otherTotes !== 1 ? 's need' : ' needs'} it${released ? ': scan the next tote' : ''}.`,
        )
      } else {
        playSound('success')
        setScanStatus(
          `✓ ${who}: ${newQtyPicked}/${ordered} ${line.item_name}`,
        )
      }

      void (async () => {
        try {
          await commitAndRecord(
            {
              line_id: line.id,
              quantity_picked: newQtyPicked,
              sku: line.item_sku,
              decrement_quantity: add,
              order_id: line.order_id,
              // Bags added at the station already went to the Zoho
              // invoice with the order change (or are logged to retry).
              item_complete: itemComplete && !opts.skipInvoice,
              order_complete: orderComplete,
            },
            `${add} ${line.item_name} into ${name ? `${name}'s tote (${line.order_id})` : line.order_id}`,
            add,
          )
        } catch (error) {
          patchPicked(line.id, prevQtyPicked)
          playSound('error')
          setScanStatus(
            `⚠ Pick for ${line.order_id} failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          )
        } finally {
          setFlavorPickingIds(prev => {
            const next = new Set(prev)
            next.delete(line.id)
            return next
          })
        }
      })()
    },
    [orders, patchPicked, commitAndRecord],
  )

  // Totes already checked out on this screen. In flavor mode a tote is scanned
  // once per bag, but checkout only needs to happen on the first scan.
  const checkedOutTotesRef = useRef<Set<string>>(new Set())

  // Flavor mode: the tote (or order) bags are going into right now. Scan the
  // tote once, then scan each bag as it goes in.
  const linesForTarget = useCallback(
    (target: PackTarget) =>
      openOrderLines(orders).filter(o =>
        (target.kind === 'tote'
          ? String(o.contact_number ?? '').trim().toUpperCase() === target.key
          : String(o.order_id) === target.key),
      ),
    [orders],
  )

  const describeNeeds = useCallback(
    (lines: OrderLine[], sku: string) => {
      const left = lines
        .filter(o => o.item_sku === sku)
        .reduce(
          (sum, o) => sum + Math.max(0, Number(o.quantity_ordered) - Number(o.quantity_picked)),
          0,
        )
      const flavorName =
        orders.find(o => o.item_sku === sku)?.item_name ??
        catalog.get(sku)?.item_name ??
        sku
      return { left, flavorName }
    },
    [orders, catalog],
  )

  const selectTarget = useCallback(
    (target: PackTarget) => {
      const lines = linesForTarget(target)
      setPackTarget(target)
      if (!lines.length) {
        const unprinted = unprintedOrderIds(orders)
        const held = heldOrderIds(orders)
        const isTarget = (o: OrderLine) =>
          target.kind === 'tote'
            ? String(o.contact_number ?? '').trim().toUpperCase() === target.key
            : String(o.order_id) === target.key
        const waiting = orders.some(o => unprinted.has(String(o.order_id)) && isTarget(o))
        const onHold = orders.some(o => held.has(String(o.order_id)) && isTarget(o))
        const who = target.kind === 'tote' ? `${target.name}'s order` : target.name
        playSound('error')
        setScanStatus(
          waiting
            ? `❌ ${who} isn't printed yet. Print its labels first.`
            : onHold
              ? `❌ ${who} isn't ready to pick yet. Mark it Ready to pick on the Orders page.`
              : `❌ ${target.name} has no open order`,
        )
        return
      }
      const bin = lowestBin(lines)
      const label = bin ? `Bin ${bin} · ${target.name}` : target.name
      if (!activeSku) {
        const bags = lines.reduce(
          (sum, o) => sum + Math.max(0, Number(o.quantity_ordered) - Number(o.quantity_picked)),
          0,
        )
        playSound('load_order')
        setScanStatus(`📦 ${label}: ${bags} bag${bags !== 1 ? 's' : ''} still to pack. Scan each bag as it goes in.`)
        return
      }
      const { left, flavorName } = describeNeeds(lines, activeSku)
      if (left > 0 && lookupSkuRef.current === activeSku) {
        setPackTarget({ ...target, flavor: activeSku })
      }
      if (left > 0) {
        playSound('load_order')
        setScanStatus(`📦 ${label}: needs ${left} ${flavorName}. Scan each bag as it goes in.`)
      } else {
        playSound('error')
        setScanStatus(`⚠ ${label} doesn't need any ${flavorName}. Check the tote.`)
      }
    },
    [linesForTarget, describeNeeds, activeSku, orders],
  )

  // Bin label for the bin being packed (#196, #197), on the Bin_Labels
  // printer: bin number, customer, bags, ship or deliver. No tote ID, since
  // bins stay at the station; its QR is the order number.
  const printToteLabel = useCallback(
    async (target: PackTarget) => {
      const lines = linesForTarget(target)
      const orderIds = Array.from(new Set(lines.map(o => String(o.order_id))))
      if (orderIds.length === 0 && target.kind === 'order') orderIds.push(target.key)
      lastActivityRef.current = Date.now()
      if (orderIds.length === 0) {
        playSound('error')
        setScanStatus(`❌ Couldn't find the order for ${target.name}'s bin label`)
        return
      }
      setPrintingLabel(true)
      try {
        const result = (await postBinLabel.trigger({
          order_ids: orderIds,
          bin: lowestBin(lines),
        })) as { success?: boolean; copies?: number } | null
        if (!result?.success || result.copies !== 1) {
          throw new Error('The printer did not accept the bin label.')
        }
        // No sound: the scan sounds are for scans, and a print isn't one.
        setScanStatus(`🖨 Bin label sent to the printer for ${target.name}`)
      } catch (error) {
        playSound('error')
        setScanStatus(`❌ Bin label didn't print: ${error instanceof Error ? error.message : 'Unknown error'}`)
      } finally {
        setPrintingLabel(false)
        lastActivityRef.current = Date.now()
        window.setTimeout(() => scanInputRef.current?.focus({ preventScroll: true }), 0)
      }
    },
    [linesForTarget, postBinLabel],
  )

  // Tote QR in flavor mode: check the tote out (first scan only) and make it
  // the tote bags are going into.
  const selectTote = useCallback(
    (customerScanId: string, toteId: string) => {
      const key = customerScanId.toUpperCase()
      const first = orders.find(
        o => String(o.contact_number ?? '').trim().toUpperCase() === key,
      )
      const name = (first && toteName(first)) || `Customer ${customerScanId}`

      // A tote label for the shared web store / Faire customer can't say
      // which order it's for. With one open order, use it; otherwise ask for
      // the order label so bags can't land in the wrong buyer's order.
      if (first?.catch_all) {
        const openIds = Array.from(
          new Set(
            openOrderLines(orders)
              .filter(o => String(o.contact_number ?? '').trim().toUpperCase() === key)
              .map(o => String(o.order_id)),
          ),
        )
        if (openIds.length !== 1) {
          playSound('error')
          setScanStatus(
            openIds.length === 0
              ? `❌ No web orders ready to pick for this tote`
              : `⚠ This tote label is shared by ${openIds.length} web orders. Scan the order label (SO…) or tap the buyer's tote instead.`,
          )
          return
        }
        const line = openOrderLines(orders).find(o => String(o.order_id) === openIds[0])!
        selectTarget({ kind: 'order', key: openIds[0]!, name: toteName(line) || openIds[0]! })
        return
      }

      if (!checkedOutTotesRef.current.has(toteId)) {
        checkedOutTotesRef.current.add(toteId)
        void (async () => {
          try {
            await processToteScan.trigger({ customer_id: customerScanId, tote_id: toteId })
          } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown error'
            // Already checked out (e.g. scanned before a page refresh) is fine.
            if (!/already checked out/i.test(msg)) {
              checkedOutTotesRef.current.delete(toteId)
              playSound('error')
              setScanStatus(`⚠ Tote ${toteId} checkout failed: ${msg}`)
            }
          }
        })()
      }
      selectTarget({ kind: 'tote', key, name })
    },
    [orders, processToteScan, selectTarget],
  )

  // Bag scan in flavor mode with a tote selected: pack one bag into it.
  const packBag = useCallback(
    (target: PackTarget, sku: string) => {
      setActiveSku(sku)
      const lines = linesForTarget(target)
      const { flavorName } = describeNeeds(lines, sku)
      const line = lines
        .filter(o => o.item_sku === sku && Number(o.quantity_picked) < Number(o.quantity_ordered))
        .sort((a, b) => String(a.order_id).localeCompare(String(b.order_id)))[0]
      if (!line) {
        const onOrder = lines.some(o => o.item_sku === sku)
        // Another bag of the flavor already waiting to be added: one more.
        if (addPrompt && addPrompt.sku === sku) {
          playSound('load_order')
          const qty = addPrompt.qty + 1
          setAddPrompt({ ...addPrompt, qty })
          setScanStatus(`➕ ${qty} ${flavorName} to add to ${addPrompt.who}'s order. Tap Add to order, or take the bags back out.`)
          return
        }
        playSound('error')
        // The order to add to: the one that already has the flavor, else
        // the tote's oldest open order.
        const orderLine =
          lines.find(o => o.item_sku === sku) ??
          [...lines].sort((a, b) => String(a.order_id).localeCompare(String(b.order_id)))[0]
        const paidOnline =
          !orderLine ||
          orderLine.catch_all ||
          String(orderLine.order_status ?? '').toLowerCase() === 'closed'
        if (paidOnline) {
          playSound('error')
          setAddPrompt(null)
          setScanStatus(
            onOrder
              ? `❌ ${target.name} already has all their ${flavorName}. Take the bag back out and scan the next tote.`
              : `❌ Wrong bag: ${target.name} didn't order ${flavorName}. Take the bag back out. (Web store and Faire orders are paid online, so bags can't be added here.)`,
          )
          return
        }
        setAddPrompt({
          sku,
          name: flavorName,
          orderId: String(orderLine.order_id),
          who: target.name,
          qty: 1,
          onOrder,
        })
        setScanStatus(
          onOrder
            ? `❌ ${target.name} already has all their ${flavorName}. Add another to the order, or take the bag back out.`
            : `❌ ${target.name} didn't order ${flavorName}. Add it to the order, or take the bag back out.`,
        )
        return
      }
      setAddPrompt(null)
      packUnits(line, 1)
    },
    [linesForTarget, describeNeeds, packUnits, addPrompt],
  )

  // "Add to order": put the prompted bags on the tote's order, then pack them.
  // The order change goes to the Zoho sales order + invoice right away (the
  // same push as UPDATEORDER), and packing them is a normal pick, so stock,
  // order status, the B2B stage and Undo all work as for any bag.
  const addToOrder = useCallback(async () => {
    const prompt = addPrompt
    lastActivityRef.current = Date.now()
    if (!prompt || adding) return
    setAdding(true)
    setScanStatus(`⏳ Adding ${prompt.qty} ${prompt.name} to ${prompt.who}'s order…`)
    try {
      const result = (await updateOrderLine.trigger({
        order_id: prompt.orderId,
        sku: prompt.sku,
        delta: prompt.qty,
        mark_picked: false,
      })) as {
        created: boolean
        line: {
          id: number | string
          item_name: string
          item_sku: string
          quantity_ordered: number
          quantity_picked: number
          quantity_printed?: number
        }
        sync: { synced: boolean; reason?: string }
      }
      applyUpdatedLine(result.line, prompt.orderId)
      const sibling = orders.find(o => String(o.order_id) === prompt.orderId)
      const existing = orders.find(
        o => String(o.order_id) === prompt.orderId && o.item_sku === prompt.sku,
      )
      const line: OrderLine = {
        ...(existing ?? sibling ?? ({} as OrderLine)),
        id: result.line.id as unknown as number,
        order_id: prompt.orderId,
        item_name: result.line.item_name,
        item_sku: result.line.item_sku,
        quantity_ordered: Number(result.line.quantity_ordered),
        quantity_picked: Number(result.line.quantity_picked),
      }
      setAddPrompt(null)
      packUnits(line, prompt.qty, { skipInvoice: true })
      // Undo takes the bags back out; say the order keeps them.
      setUndoStack(prev => {
        const last = prev[prev.length - 1]
        if (!last || last.parts[0]?.line_id !== line.id) return prev
        return [...prev.slice(0, -1), { ...last, added: true, label: `${last.label} (added to order)` }]
      })
      const bags = `${prompt.qty} ${result.line.item_name}`
      setScanStatus(prev =>
        result.sync?.synced === false
          ? `⚠ Added ${bags} to ${prompt.orderId} and packed, but Zoho didn't update: ${result.sync.reason ?? 'unknown error'}. Retry it from the Dashboard alerts.`
          : prev.startsWith('🎉')
            ? `🎉 Added ${bags} to ${prompt.who}'s order. ${prev.replace(/^\S+\s*/u, '')}`
            : `✅ Added ${bags} to ${prompt.who}'s order (${prompt.orderId}) and packed. Scan the next bag.`,
      )
    } catch (error) {
      playSound('error')
      setScanStatus(
        `⚠ Couldn't add ${prompt.name} to ${prompt.orderId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    } finally {
      setAdding(false)
      scanInputRef.current?.focus()
    }
  }, [addPrompt, adding, updateOrderLine, applyUpdatedLine, orders, packUnits])

  const cancelAdd = useCallback(() => {
    if (!addPrompt) return
    setAddPrompt(null)
    setScanStatus(`Take the ${addPrompt.name} back out. Scan the next bag.`)
    scanInputRef.current?.focus()
  }, [addPrompt])

  // The prompt belongs to the selected tote: drop it when the tote changes.
  useEffect(() => {
    setAddPrompt(prev => (prev && prev.who !== packTarget?.name ? null : prev))
  }, [packTarget])

  // Undo the newest pick on this screen (a bag scan, Pick All, or Pick rest):
  // picked count, stock, a completed order's status and the B2B stage go back.
  // A line already sent to the Zoho invoice stays on it (there's no reverse);
  // the status says so, and scanning it again won't send it twice.
  const undoLast = useCallback(async () => {
    const entry = undoStack[undoStack.length - 1]
    lastActivityRef.current = Date.now()
    if (undoing) return
    if (!entry) {
      playSound('error')
      setScanStatus('⚠ Nothing to undo on this screen')
      return
    }
    setUndoing(true)
    setScanStatus(`⏳ Undoing ${entry.label}…`)
    let onInvoice = false
    let bags = 0
    const failures: string[] = []
    try {
      for (const part of [...entry.parts]) {
        const res = await part.result
        if (res) {
          const r = (await undoPick.trigger({
            line_id: part.line_id,
            units: part.units,
            sku: part.sku,
            stock_taken: Number(res.stock_taken ?? 0),
            batch_oz_taken: Number(res.batch_oz_taken ?? 0),
            order_id: part.order_id,
            order_completed: Boolean(res.order_completed),
            previous_status: res.previous_status ?? null,
          })) as {
            quantity_picked: number
            reopened: boolean
            on_invoice: boolean
            failures?: string[]
          }
          const status = res.previous_status || 'open'
          setOrders(prev =>
            prev.map(o =>
              o.id === part.line_id
                ? {
                    ...o,
                    quantity_picked: Number(r.quantity_picked),
                    ...(r.reopened ? { order_status: status } : {}),
                  }
                : r.reopened && String(o.order_id) === String(part.order_id)
                  ? { ...o, order_status: status }
                  : o,
            ),
          )
          setFinishedHere(prev => {
            if (!prev.has(String(part.order_id))) return prev
            const next = new Set(prev)
            next.delete(String(part.order_id))
            return next
          })
          onInvoice = onInvoice || r.on_invoice || Boolean(res.invoice_sent)
          failures.push(...(r.failures ?? []))
          bags += part.units
        }
        // Done with this line: a retry after a later failure won't undo it twice.
        entry.parts = entry.parts.filter(p => p !== part)
      }
      setUndoStack(prev => prev.filter(e => e.key !== entry.key))
      playSound('load_order')
      setScanStatus(
        `↩ Undid ${entry.label}.` +
          (bags > 0 ? ` Take ${bags === 1 ? 'the bag' : `the ${bags} bags`} back out.` : '') +
          (entry.added
            ? " The added bags stay on the order and its Zoho invoice; change them on the Orders page or in Zoho if the customer shouldn't get them."
            : onInvoice
              ? " It was already added to the Zoho invoice and stays there; fix the invoice in Zoho if the customer shouldn't get it."
              : '') +
          (failures.length ? ` (${failures.join('; ')})` : ''),
      )
    } catch (error) {
      playSound('error')
      setScanStatus(
        `⚠ Undo failed: ${error instanceof Error ? error.message : 'Unknown error'}. Tap Undo to try again.`,
      )
    } finally {
      setUndoing(false)
      scanInputRef.current?.focus()
    }
  }, [undoStack, undoing, undoPick])

  // Table columns
  const columns = useMemo<ColumnDef<PickListRow>[]>(
    () => [
      { accessorKey: 'item_name', header: 'Item Name' },
      { accessorKey: 'item_sku', header: 'SKU' },
      { accessorKey: 'quantity_ordered', header: 'Ordered' },
      { accessorKey: 'quantity_picked', header: 'Picked' },
      { accessorKey: 'quantity_remaining', header: 'Remaining' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={
              row.original.status === 'Complete' ? 'default' :
              row.original.status === 'Not Started' ? 'outline' : 'secondary'
            }
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original
          const key = `${r.item_sku}__${r.item_name}`
          const picking = pickingRows.has(key)
          const done = r.quantity_remaining === 0
          return (
            <Button
              size="sm"
              variant="outline"
              disabled={done || picking}
              onClick={() => void pickAllRow(r)}
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              {picking ? 'Picking…' : 'Pick All'}
            </Button>
          )
        },
      },
    ],
    [pickingRows, pickAllRow]
  )

  const table = useReactTable({
    data: pickListData,
    columns,
    state: { sorting, globalFilter: filterValue },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFilterValue,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  // Tapping a button on a tablet moves focus off the scan box, and the next
  // scan would be lost. Put focus back after any tap outside a text field.
  useEffect(() => {
    const onPointerUp = (e: PointerEvent) => {
      lastActivityRef.current = Date.now()
      const el = e.target as HTMLElement | null
      if (!el || el.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]')) return
      window.setTimeout(() => {
        const active = document.activeElement as HTMLElement | null
        if (active && active.matches('input, textarea, select')) return
        scanInputRef.current?.focus({ preventScroll: true })
      }, 0)
    }
    document.addEventListener('pointerup', onPointerUp)
    return () => document.removeEventListener('pointerup', onPointerUp)
  }, [])

  // Load data on mount; focus input after load
  useEffect(() => {
    getAllOrderLines.trigger()
    getProducts.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pre-select order from URL param (?order=SO123) once data arrives
  useEffect(() => {
    if (hasAppliedUrlOrder.current) return
    const orderFromUrl = searchParams.get('order')
    if (!orderFromUrl || orders.length === 0) return
    hasAppliedUrlOrder.current = true
    const exists = orders.some(o => String(o.order_id) === orderFromUrl)
    if (exists) {
      setCurrentOrderId(orderFromUrl)
      setScanStatus(`✅ Order ${orderFromUrl} selected. Now scan SKUs.`)
    } else {
      setScanStatus(`❌ Order ${orderFromUrl} not found`)
    }
  }, [orders, searchParams])

  useEffect(() => {
    if (!getAllOrderLines.loading) {
      scanInputRef.current?.focus()
    }
  }, [getAllOrderLines.loading])

  // Periodically refresh order data so the interface always reflects the
  // latest state. We only refresh when the tab is visible and the station has
  // been idle for a few seconds, so an in-progress pick is never clobbered.
  const refreshRef = useRef<() => void>(() => {})
  refreshRef.current = () => {
    if (getAllOrderLines.loading) return
    void getAllOrderLines.trigger(undefined, { skipCache: true })
  }
  useEffect(() => {
    const REFRESH_INTERVAL_MS = 7 * 60_000
    const IDLE_THRESHOLD_MS = 8_000
    const id = window.setInterval(() => {
      if (document.hidden) return
      if (Date.now() - lastActivityRef.current < IDLE_THRESHOLD_MS) return
      refreshRef.current()
    }, REFRESH_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [])

  // By flavor: let go of the selected tote after a minute with no scans or
  // taps, as if Done with tote was tapped, so the next bag isn't packed into a
  // tote that's already been put away.
  useEffect(() => {
    if (!packTarget) return
    const TOTE_IDLE_MS = 60_000
    const id = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current < TOTE_IDLE_MS) return
      setPackTarget(null)
      setScanStatus(`Done with ${packTarget.name} after 1 minute with no scans. Scan the next tote.`)
    }, 5_000)
    return () => window.clearInterval(id)
  }, [packTarget])

  // ─── Scan logic (mirrors processScanWithValidation.js) ──────────────────────

  const processScan = useCallback(async () => {
    const raw = scanInputValue.trim()
    lastActivityRef.current = Date.now()

    if (!raw) {
      playSound('error')
      setScanStatus('⚠ Please scan an order number (SO...) or an item SKU')
      setScanInputValue('')
      return
    }

    // ── UNDO barcode: same as tapping Undo last scan ──
    if (/^UNDO$/i.test(raw)) {
      setScanInputValue('')
      void undoLast()
      return
    }

    // A tote or order scan moves on from a bag waiting to be added (a bag
    // scan handles the prompt itself).
    if (/^(PWT\||SO|WC|UPDATEORDER$)/i.test(raw)) setAddPrompt(null)

    // ── UPDATEORDER mode toggle ──
    // Scanning the UPDATEORDER barcode switches the station into "add items to
    // an order" mode: scan an order (SO…), then scan products to add/increment.
    if (/^UPDATEORDER$/i.test(raw)) {
      setUpdateMode(true)
      setLastScannedSku('')
      setLastScannedOrderId('')
      playSound('load_order')
      setScanStatus('🛠 Update Order mode ON. Scan an order (SO…), then scan products to add.')
      setScanInputValue('')
      return
    }

    // ── New combined tote QR ───────────────────────────────────────────────
//
// New tote labels contain everything needed in one QR:
//
//   PWT|CUSTOMER_ID|TOTE_ID
//
// Example:
//
//   PWT|123456|7K4MX9QP
//
// In normal mode:
//   The tote is checked OUT to the customer encoded in the QR.
//
// In Tote Check-In mode:
//   The customer portion is ignored and the tote is checked IN.
//
// Check-In mode automatically turns itself off after one successful scan
// so it cannot accidentally remain enabled.

if (/^PWT\|/i.test(raw)) {
  setScanInputValue('')

  /*
   * Break the QR into:
   *
   *   [0] PWT
   *   [1] customer/contact number
   *   [2] tote ID
   */
  const parts = raw.split('|')

  if (
    parts.length !== 3 ||
    parts[0]?.trim().toUpperCase() !== 'PWT'
  ) {
    playSound('error')
    setScanStatus('⚠ Invalid tote QR code')
    return
  }

  const customerId =
    parts[1]?.trim() ?? ''

  const toteId =
    parts[2]?.trim().toUpperCase() ?? ''

  /*
   * Both values must exist even during check-in.
   *
   * Requiring the complete format helps catch damaged or incorrectly
   * generated tote labels.
   */
  if (!customerId || !toteId) {
    playSound('error')
    setScanStatus(
      '⚠ Tote QR code is missing customer or tote information'
    )
    return
  }

  /*
   * New tote IDs are alphanumeric.
   *
   * This also accepts old numeric IDs if they are ever encoded into
   * the new QR format.
   */
  if (!/^[A-Z0-9]{1,20}$/.test(toteId)) {
    playSound('error')
    setScanStatus(`⚠ Invalid tote ID: ${toteId}`)
    return
  }


  // ── CHECK-IN MODE ────────────────────────────────────────────────

  if (toteCheckInMode) {
    try {
      await checkInTote.trigger({
        tote_id: toteId,
      })

      playSound('success')

      setScanStatus(
        `✓ Tote ${toteId} checked in`
      )

      /*
       * Check-In mode is intentionally one-shot.
       *
       * After a successful check-in, return the scanner to its normal
       * checkout/picking behavior.
       */
      setToteCheckInMode(false)
    } catch (error) {
      playSound('error')

      setScanStatus(
        `⚠ Tote ${toteId} check-in failed: ${
          error instanceof Error
            ? error.message
            : 'Unknown error'
        }`
      )

      /*
       * Leave Check-In mode ON after a failure.
       *
       * This lets the operator fix/retry the scan without having to
       * click Check In Tote again.
       */
    }

    return
  }


  // ── FLAVOR MODE = PACK A BAG INTO THIS TOTE ──────────────────────

  if (pickMode === 'flavor' && !updateMode) {
    selectTote(customerId, toteId)
    return
  }

  // ── NORMAL MODE = CHECKOUT ───────────────────────────────────────

  try {
    const tote =
      await processToteScan.trigger({
        customer_id: customerId,
        tote_id: toteId,
      })

    if (!tote) {
      playSound('error')

      setScanStatus(
        `⚠ Tote ${toteId} not checked out — customer ${customerId} not found`
      )

      return
    }

    playSound('success')

    setScanStatus(
      `✓ Tote ${toteId} checked out for customer ${customerId}`
    )
  } catch (error) {
    playSound('error')

    setScanStatus(
      `⚠ Tote ${toteId} checkout failed: ${
        error instanceof Error
          ? error.message
          : 'Unknown error'
      }`
    )
  }

  return
}
    // ── Flavor mode: scan the tote (QR, handled above) or order label once,
    // then scan each bag as it goes in. With no tote selected, a bag scan
    // shows which totes need that flavor. ──
    if (pickMode === 'flavor' && !updateMode) {
      if (/^SO/i.test(raw) || /^WC/i.test(raw)) {
        setScanInputValue('')
        const orderLines = orders.filter(r => r.order_id === raw)
        if (!orderLines.length) {
          playSound('error')
          setScanStatus(`❌ Order ${raw} not found`)
          return
        }
        if (!isPackableStatus(orderLines[0]?.order_status)) {
          playSound('error')
          setScanStatus(`❌ Order ${raw} is already complete`)
          return
        }
        const name = toteName(orderLines[0]!)
        selectTarget({ kind: 'order', key: raw, name: name ? `${name} (${raw})` : `Order ${raw}` })
        return
      }
      if (!/^CUS/i.test(raw) && !(/^\d+$/.test(raw) && raw.length < 10)) {
        setScanInputValue('')
        const known = orders.some(o => o.item_sku === raw)
        // With a tote selected, any product can be added to its order.
        if (packTarget && (known || catalog.has(raw))) {
          packBag(packTarget, raw)
          return
        }
        setAddPrompt(null)
        if (!known) {
          playSound('error')
          setScanStatus(
            catalog.has(raw)
              ? `⚠ ${catalog.get(raw)!.item_name} isn't on any order. To add it to one, scan that tote first, then the bag.`
              : `⚠ ${raw} isn't on any order`,
          )
          return
        }
        selectFlavor(raw)
        return
      }
      // CUS… and short numeric tote scans fall through to the shared handling.
    }

    // ── Order scan ──
    if (/^SO/i.test(raw) || /^WC/i.test(raw)) {
      const orderId = raw

      // In update mode, once at least one product has been added to the current
      // order, scanning a *different* order exits update mode and treats the
      // new scan as a normal order selection.
      let effectiveUpdateMode = updateMode
      if (
        updateMode &&
        lastScannedSku &&
        lastScannedOrderId === currentOrderId &&
        orderId !== currentOrderId
      ) {
        flushPendingSync()
        setUpdateMode(false)
        setLastScannedSku('')
        setLastScannedOrderId('')
        effectiveUpdateMode = false
      }

      const orderLines = orders.filter(r => r.order_id === orderId)

      if (!orderLines.length) {
        // In update mode an order may legitimately have no lines yet, so accept
        // any order that exists in the loaded set; otherwise reject unknowns.
        playSound('error')
        setScanStatus(`❌ Order ${orderId} not found`)
        setScanInputValue('')
        return
      }

      // In update mode we allow selecting a fully-picked/complete order so items
      // can still be added to it.
      if (!effectiveUpdateMode) {
        const allComplete = orderLines.every(
          r => Number(r.quantity_picked) >= Number(r.quantity_ordered)
        )
        if (allComplete) {
          playSound('error')
          setScanStatus(`❌ Order ${orderId} already complete`)
          setScanInputValue('')
          return
        }
      }

      setCurrentOrderId(orderId)
      playSound('load_order')
      setScanStatus(
        effectiveUpdateMode
          ? `✅ Order ${orderId} selected. Scan products to add/increment.`
          : `✅ Order ${orderId} selected. Now scan SKUs.`
      )
      setScanInputValue('')
      return
    }

    // ── Product scan while in UPDATEORDER mode ──
    // Any non-order scan is treated as a product SKU to add to the active order.
    if (updateMode) {
      if (!currentOrderId) {
        playSound('error')
        setScanStatus('⚠ Scan an order number first, then scan products to add')
        setScanInputValue('')
        return
      }
      const sku = raw
      setScanInputValue('')
      try {
        const result = (await updateOrderLine.trigger({
          order_id: currentOrderId,
          sku,
          delta: 1,
          defer_sync: true,
        })) as {
          created: boolean
          line: {
            id: number | string
            item_name: string
            item_sku: string
            quantity_ordered: number
            quantity_picked: number
          }
          sync: { synced: boolean; reason?: string }
          syncPayload?: SyncPayload
        }
        applyUpdatedLine(result.line, currentOrderId)
        if (result.syncPayload) queueSync(result.syncPayload)
        setLastScannedSku(sku)
        setLastScannedOrderId(currentOrderId)
        playSound('success')
        const qty = Number(result.line.quantity_ordered)
        const verb = result.created ? 'Added' : 'Updated'
        setScanStatus(
          `✓ ${verb} ${result.line.item_name} on ${currentOrderId} — qty ${qty} (picked, queued)`
        )
      } catch (error) {
        playSound('error')
        setScanStatus(
          `⚠ Could not add ${sku}: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      }
      return
    }

    // ── Customer scan (CUS...) ──
    if (/^CUS/i.test(raw)) {
      setCurrentCustomerId(raw)
      playSound('load_order')
      setScanStatus(`✅ Customer ${raw} selected. Now scan a tote.`)
      setScanInputValue('')
      return
    }

    // ── Tote scan (all numbers, < 10 digits) ──
    if (/^\d+$/.test(raw) && raw.length < 10) {
      if (!currentCustomerId) {
        // No customer selected: check the tote back in, clearing its
        // customer association (customer_id, customer, address -> null).
        setScanInputValue('')
        try {
          await checkInTote.trigger({ tote_id: raw })
          playSound('success')
          setScanStatus(`✓ Tote ${raw} checked in`)
        } catch (error) {
          playSound('error')
          setScanStatus(
            `⚠ Tote ${raw} check-in failed: ${error instanceof Error ? error.message : 'Unknown error'}`
          )
        }
        return
      }
      setScanInputValue('')
      try {
        const tote = await processToteScan.trigger({
          customer_id: currentCustomerId,
          tote_id: raw,
        })
        // Only report success when the tote row was actually written.
        if (!tote) {
          playSound('error')
          setScanStatus(
            `⚠ Tote ${raw} not checked out — customer ${currentCustomerId} not found`
          )
          return
        }
        playSound('success')
        setScanStatus(
          `✓ Tote ${raw} checked out for customer ${currentCustomerId}`
        )
      } catch (error) {
        playSound('error')
        setScanStatus(
          `⚠ Tote ${raw} checkout failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      }
      return
    }

    const itemSku = raw

    // ── SKU scan – requires an active order ──
    if (!currentOrderId) {
      playSound('error')
      setScanStatus('⚠ Scan an order number first to select an order')
      setScanInputValue('')
      return
    }

    const itemCheck = orders.find(
      r => r.order_id === currentOrderId && r.item_sku === itemSku
    )

    if (!itemCheck) {
      playSound('error')
      setScanStatus(`⚠ Item ${itemSku} not found for order ${currentOrderId}`)
      setScanInputValue('')
      return
    }

    if (Number(itemCheck.quantity_picked) >= Number(itemCheck.quantity_ordered)) {
      playSound('error')
      setScanStatus(`⚠ Item ${itemSku} already fully picked`)
      setScanInputValue('')
      return
    }

    const newQtyPicked = Number(itemCheck.quantity_picked) + 1
    const prevQtyPicked = Number(itemCheck.quantity_picked)

    // ── Optimistic update ──
    // Apply the pick locally and give instant audio/visual feedback so the
    // operator can scan the next item immediately. Persistence happens in the
    // background; we only revert if the write fails.
    patchPicked(itemCheck.id, newQtyPicked)
    setLastScannedSku(itemSku)
    setLastScannedOrderId(currentOrderId)
    setScanInputValue('')

    const orderLines = orders.filter(r => r.order_id === currentOrderId)
    const willBeItemComplete = newQtyPicked >= Number(itemCheck.quantity_ordered)
    const allOrderComplete = orderLines.every(r => {
      if (r.item_sku === itemSku) {
        return newQtyPicked >= Number(itemCheck.quantity_ordered)
      }
      return Number(r.quantity_picked) >= Number(r.quantity_ordered)
    })

    if (allOrderComplete) {
      playSound('complete')
      setScanStatus(`🎉 Order ${currentOrderId} COMPLETE! All items picked`)
    } else if (willBeItemComplete) {
      playSound('complete')
      setScanStatus(`✅ Item ${itemSku} COMPLETE for order ${currentOrderId}`)
    } else {
      playSound('success')
      setScanStatus(
        `✓ Scanned ${itemSku} for order ${currentOrderId} (${newQtyPicked}/${Number(itemCheck.quantity_ordered)})`
      )
    }

    // ── Background persistence (single batched round-trip) ──
    void (async () => {
      try {
        await commitAndRecord(
          {
            line_id: itemCheck.id,
            quantity_picked: newQtyPicked,
            sku: itemSku,
            decrement_quantity: 1,
            order_id: currentOrderId,
            item_complete: willBeItemComplete,
            order_complete: allOrderComplete,
          },
          `1 ${itemCheck.item_name} for ${currentOrderId}`,
          1,
        )
      } catch (error) {
        // Revert the optimistic pick on failure
        patchPicked(itemCheck.id, prevQtyPicked)
        playSound('error')
        setScanStatus(
          `⚠ Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      }
    })()
  }, [
    patchPicked,
    commitAndRecord,
    undoLast,
    scanInputValue,
    orders,
    currentOrderId,
    currentCustomerId,
    toteCheckInMode,
    processToteScan,
    checkInTote,
    updateMode,
    updateOrderLine,
    applyUpdatedLine,
    queueSync,
    flushPendingSync,
    lastScannedSku,
    lastScannedOrderId,
    pickMode,
    activeSku,
    packUnits,
    selectFlavor,
    selectTote,
    selectTarget,
    packBag,
    packTarget,
    catalog,
  ])

  // ─── Toolbar actions ──────────────────────────────────────────────────────

  const pickAll = useCallback(async () => {
    const item = lastScannedItem
    if (!item) return

    const ordered = Number(item.quantity_ordered)
    const prevQtyPicked = Number(item.quantity_picked)
    const remaining = ordered - prevQtyPicked
    if (remaining <= 0) {
      setScanStatus(`⚠ Item ${item.item_sku} already fully picked`)
      return
    }

    // ── Optimistic update ── fill the line locally and give instant feedback.
    patchPicked(item.id, ordered)

    const orderLines = orders.filter(r => r.order_id === item.order_id)
    const allOrderComplete = orderLines.every(r => {
      if (r.id === item.id) return true
      return Number(r.quantity_picked) >= Number(r.quantity_ordered)
    })

    playSound('complete')
    if (allOrderComplete) {
      setScanStatus(`🎉 Order ${item.order_id} COMPLETE! All items picked`)
    } else {
      setScanStatus(`✅ Item ${item.item_sku} COMPLETE for order ${item.order_id}`)
    }

    setLastScannedSku('')
    setLastScannedOrderId('')
    scanInputRef.current?.focus()

    // ── Background persistence (single batched round-trip) ──
    void (async () => {
      try {
        await commitAndRecord(
          {
            line_id: item.id,
            quantity_picked: ordered,
            sku: item.item_sku,
            decrement_quantity: remaining,
            order_id: item.order_id,
            item_complete: true,
            order_complete: allOrderComplete,
          },
          `Pick All: ${remaining} ${item.item_name} for ${item.order_id}`,
          remaining,
        )
      } catch (error) {
        patchPicked(item.id, prevQtyPicked)
        playSound('error')
        setScanStatus(
          `⚠ Pick All failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
      }
    })()
  }, [
    lastScannedItem,
    orders,
    patchPicked,
    commitAndRecord,
  ])

  const handleDownloadCSV = useCallback(() => {
    const rows = table.getFilteredRowModel().rows
    const headers = [
      'Item Name',
      'SKU',
      'Ordered',
      'Picked',
      'Remaining',
      'Status',
    ]
    const csvRows = [
      headers.join(','),
      ...rows.map(r => {
        const d = r.original
        return [
          `"${d.item_name.replace(/"/g, '""')}"`,
          d.item_sku,
          d.quantity_ordered,
          d.quantity_picked,
          d.quantity_remaining,
          d.status,
        ].join(',')
      }),
    ]
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'pick-list.csv'
    a.click()
    URL.revokeObjectURL(url)
  }, [table])

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      style={{ padding: station ? '10px 16px' : '8px 12px' }}
      className={cn(
        'scan-page flex flex-col gap-3 min-h-screen bg-background overflow-y-auto',
        station && 'scan-station',
      )}
    >
      {station ? (
        /* Station top bar: title, counts and refresh in one line */
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-foreground tracking-tight mr-auto">
            {title}
          </h1>
          {([
            { label: 'Orders', value: stats.totalOrders },
            { label: 'To pick', value: stats.totalItems },
            { label: 'Picked', value: stats.pickedItems },
            { label: 'Left', value: stats.remainingItems },
          ] as const).map(s => (
            <span key={s.label} className="flex items-baseline gap-1.5 rounded-lg border bg-card px-3 py-1.5">
              <span className="text-2xl font-bold text-foreground leading-none">{s.value}</span>
              <span className="text-sm text-muted-foreground">{s.label}</span>
            </span>
          ))}
          <Button
            variant="outline"
            className="h-12 w-12 p-0"
            onClick={() => void getAllOrderLines.trigger(undefined, { skipCache: true })}
            disabled={getAllOrderLines.loading}
            aria-label="Refresh"
            title="Refresh orders"
          >
            <RefreshCw className={cn('w-5 h-5', getAllOrderLines.loading && 'animate-spin')} />
          </Button>
        </div>
      ) : (
      /* Stats: small cards in one row so the scan box and pick lists fit on an iPad */
      <div className="grid grid-cols-4 gap-2">
        {([
          { label: 'Open Orders', value: stats.totalOrders },
          { label: 'Items to Pick', value: stats.totalItems },
          { label: 'Picked', value: stats.pickedItems },
          { label: 'Remaining', value: stats.remainingItems },
        ] as const).map(s => (
          <div
            key={s.label}
            className="flex flex-col items-center justify-center px-2 py-1.5 rounded-lg border bg-card"
          >
            <span className="text-[11px] text-muted-foreground uppercase tracking-wide text-center leading-tight">
              {s.label}
            </span>
            <span className="text-xl font-bold text-foreground leading-tight">
              {s.value}
            </span>
          </div>
        ))}
      </div>
      )}

{/* Scanning station */}
<div className="border rounded-lg p-3 bg-card flex flex-col gap-2">

  {/* Header / mode controls */}
  <div className="flex items-center gap-2 flex-wrap">
    {!station && (
      <h2 className="text-xl font-semibold text-foreground">
        Scanning Station
      </h2>
    )}

    {/* Pick mode: by order or by flavor (the Scan Station has just one) */}
    {!updateMode && !station && (
      <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Pick mode">
        {([
          { mode: 'order', label: 'By order' },
          { mode: 'flavor', label: 'By flavor' },
        ] as const).map(m => (
          <Button
            key={m.mode}
            size="sm"
            variant={pickMode === m.mode ? 'default' : 'ghost'}
            className="h-10 px-4 text-base"
            aria-pressed={pickMode === m.mode}
            onClick={() => {
              if (pickMode !== m.mode) setPickMode(m.mode)
            }}
          >
            {m.label}
          </Button>
        ))}
      </div>
    )}

    {/* Tote check-in mode */}
    {!updateMode && (
      <Button
        variant={toteCheckInMode ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          const next = !toteCheckInMode

          setToteCheckInMode(next)

          if (next) {
            setCurrentCustomerId('')
            setScanStatus('Scan a tote QR to check it in')
          } else {
            setScanStatus('Ready to scan...')
          }

          window.setTimeout(() => {
            scanInputRef.current?.focus()
          }, 0)
        }}
      >
        {toteCheckInMode
          ? 'Cancel Tote Check-In'
          : 'Check In Tote'}
      </Button>
    )}

    {/* Update Order mode badge */}
    {updateMode && (
      <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-500 dark:bg-amber-600">
        <PackagePlus className="w-3.5 h-3.5" />
        Update Order Mode
      </Badge>
    )}

    {/* Exit Update Order mode */}
    {updateMode && (
      <Button
        variant="ghost"
        size="sm"
        className="px-3 text-sm"
        onClick={() => {
          flushPendingSync()
          setUpdateMode(false)
          setLastScannedSku('')
          setLastScannedOrderId('')
          scanInputRef.current?.focus()
        }}
      >
        <X className="w-3.5 h-3.5 mr-1" />
        Exit
      </Button>
    )}
  </div>

  {/* Scanner input */}
  <div className="flex flex-col gap-1">
    <label
      className={cn(
        'text-sm font-medium text-foreground',
        // The placeholder says the same thing; save the line in By flavor.
        pickMode === 'flavor' && !updateMode && !toteCheckInMode && 'sr-only',
      )}
    >
      {updateMode
        ? 'Scan an order (SO…), then scan products to add'
        : toteCheckInMode
          ? 'Scan tote QR to check in'
          : pickMode === 'flavor'
            ? 'Scan a tote, then each bag as it goes in'
            : 'Scan Order/SKU or Tote QR'}
    </label>

    <div className="flex gap-2">
    <Input
      ref={scanInputRef}
      value={scanInputValue}
      onChange={e => setScanInputValue(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          void processScan()
        }
      }}
      placeholder={
        updateMode
          ? 'Scan SO number, then scan product SKUs to add…'
          : toteCheckInMode
            ? 'Scan tote QR to check in…'
            : pickMode === 'flavor'
              ? station
                ? 'Scan a tote, an order label, or a bag…'
                : 'Scan a tote QR, then each bag…'
              : 'Scan SO number, product SKU, or tote QR…'
      }
      className="h-12 text-lg flex-1"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      inputMode={showKeyboard ? 'text' : 'none'}
    />
    <Button
      type="button"
      variant={showKeyboard ? 'default' : 'outline'}
      className="h-12 w-12 shrink-0 p-0"
      aria-label={showKeyboard ? 'Hide on-screen keyboard' : 'Type a code'}
      title={showKeyboard ? 'Hide on-screen keyboard' : 'Type a code'}
      onClick={() => {
        setShowKeyboard(v => !v)
        // Re-focus so the change takes effect right away.
        scanInputRef.current?.blur()
        window.setTimeout(() => scanInputRef.current?.focus(), 0)
      }}
    >
      <Keyboard className="w-5 h-5" />
    </Button>
    </div>
  </div>

  {/* Scanner status */}
  <p
    className={cn(
      'text-xl font-medium min-h-[3rem] rounded-md px-3 py-2 flex items-center',
      /^[❌⚠]/u.test(scanStatus)
        ? 'bg-red-100 text-red-900 dark:bg-red-950/50 dark:text-red-100'
        : /^↩/u.test(scanStatus)
          ? 'bg-amber-100 text-amber-950 dark:bg-amber-950/50 dark:text-amber-100'
        : /^[✓✅🎉]/u.test(scanStatus)
          ? 'bg-green-100 text-green-900 dark:bg-green-950/50 dark:text-green-100'
          : 'bg-muted text-foreground',
    )}
    role="status"
    aria-live="polite"
  >
    {scanStatus}
  </p>

  {/* Add a bag the tote's order doesn't have */}
  {addPrompt && pickMode === 'flavor' && !updateMode && (
    <div className="flex items-center gap-3 flex-wrap rounded-md border-2 border-amber-400 bg-amber-50 p-3 dark:border-amber-600 dark:bg-amber-950/40">
      <div className="flex flex-col min-w-0 mr-auto">
        <span className="text-lg font-semibold text-foreground">
          {addPrompt.onOrder ? 'Add more' : 'Add'} {addPrompt.name} to {addPrompt.who}'s order?
        </span>
        <span className="text-sm text-muted-foreground">
          {addPrompt.orderId} · goes on the Zoho order and invoice · scan more bags of it to add more
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1" role="group" aria-label="Bags to add">
        <Button
          variant="outline"
          className="h-12 w-12 p-0"
          aria-label="One less"
          disabled={addPrompt.qty <= 1 || adding}
          onClick={() => setAddPrompt(p => (p ? { ...p, qty: Math.max(1, p.qty - 1) } : p))}
        >
          <Minus className="w-5 h-5" />
        </Button>
        <span className="w-12 text-center text-2xl font-bold tabular-nums" aria-live="polite">
          {addPrompt.qty}
        </span>
        <Button
          variant="outline"
          className="h-12 w-12 p-0"
          aria-label="One more"
          disabled={adding}
          onClick={() => setAddPrompt(p => (p ? { ...p, qty: p.qty + 1 } : p))}
        >
          <Plus className="w-5 h-5" />
        </Button>
      </div>
      <Button className="h-12 px-5 text-base" onClick={() => void addToOrder()} disabled={adding}>
        <PackagePlus className="w-5 h-5 mr-1.5" />
        {adding ? 'Adding…' : `Add ${addPrompt.qty} to order`}
      </Button>
      <Button variant="ghost" className="h-12 px-4 text-base" onClick={cancelAdd} disabled={adding}>
        Cancel
      </Button>
      </div>
    </div>
  )}

  {/* Undo last scan */}
  {!updateMode && undoStack.length > 0 && (
    <div className="flex items-center gap-2 min-w-0">
      <Button
        variant="outline"
        className="shrink-0"
        onClick={() => void undoLast()}
        disabled={undoing}
      >
        <Undo2 className="w-4 h-4 mr-1" />
        {undoing ? 'Undoing…' : 'Undo last scan'}
      </Button>
      <span className="text-sm text-muted-foreground truncate">
        {undoStack[undoStack.length - 1]?.label}
      </span>
    </div>
  )}

  {/* Last scanned item / Pick All */}
  {!updateMode &&
    pickMode === 'order' &&
    lastScannedItem &&
    Number(lastScannedItem.quantity_ordered) -
      Number(lastScannedItem.quantity_picked) >
      0 && (
      <div className="flex items-center gap-3 rounded-md border border-primary/40 bg-primary/5 p-3">
        <div className="flex flex-col">
          <span className="text-lg font-semibold text-foreground">
            {lastScannedItem.item_name}
          </span>

          <span className="text-xs text-muted-foreground">
            SKU {lastScannedItem.item_sku} ·{' '}
            {Number(lastScannedItem.quantity_picked)}/
            {Number(lastScannedItem.quantity_ordered)} picked ·{' '}
            {Number(lastScannedItem.quantity_ordered) -
              Number(lastScannedItem.quantity_picked)}{' '}
            remaining
          </span>
        </div>

        <Button
          className="ml-auto"
          onClick={() => void pickAll()}
          disabled={commitPick.loading}
        >
          <CheckCheck className="w-4 h-4 mr-1" />
          Pick All
        </Button>
      </div>
    )}

  {/* Active order */}
  {currentOrderId && (pickMode === 'order' || updateMode) && (
    <div className="flex items-center gap-2 text-base">
      <span className="text-muted-foreground">
        Active order:
      </span>

      <span className="font-semibold text-foreground">
        {currentOrderId}
      </span>

      <Button
        variant="ghost"
        size="sm"
        className="px-3 text-sm"
        onClick={() => {
          setCurrentOrderId('')
          setLastScannedSku('')
          setLastScannedOrderId('')
          setScanStatus('Ready to scan...')
        }}
      >
        Clear
      </Button>
    </div>
  )}

  {/* Legacy active customer */}
  {currentCustomerId && (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">
        Active customer:
      </span>

      <span className="font-semibold text-foreground">
        {currentCustomerId}
      </span>

      <Button
        variant="ghost"
        size="sm"
        className="px-3 text-sm"
        onClick={() => {
          setCurrentCustomerId('')
          setScanStatus('Ready to scan...')
        }}
      >
        Clear
      </Button>
    </div>
  )}
</div>
      {pickMode === 'flavor' && !updateMode ? (
        <FlavorPickPanel
          lines={orders}
          activeSku={activeSku}
          loading={getAllOrderLines.loading}
          pickingIds={flavorPickingIds}
          finishedHere={finishedHere}
          target={
            packTarget
              ? {
                  name: packTarget.name,
                  orderIds: new Set(linesForTarget(packTarget).map(o => String(o.order_id))),
                  bin: lowestBin(linesForTarget(packTarget)),
                }
              : null
          }
          onPrintLabel={packTarget ? () => void printToteLabel(packTarget) : undefined}
          printingLabel={printingLabel}
          onClearTarget={() => {
            setPackTarget(null)
            setScanStatus('Scan the next tote')
            scanInputRef.current?.focus()
          }}
          onSelectFlavor={selectFlavor}
          onClearFlavor={() => {
            setActiveSku('')
            lookupSkuRef.current = ''
            setScanStatus('Scan a bag of a flavor to see which totes need it')
            scanInputRef.current?.focus()
          }}
          simple={station}
          onPackInto={line => {
            // Same as scanning the tote's QR (or the order label when the
            // customer has no tote number), without the tote checkout.
            // Web store / Faire orders share one customer, so each is its own tote.
            const key = line.catch_all ? '' : String(line.contact_number ?? '').trim().toUpperCase()
            const name = toteName(line) || String(line.order_id)
            lastActivityRef.current = Date.now()
            selectTarget(
              key
                ? { kind: 'tote', key, name }
                : { kind: 'order', key: String(line.order_id), name },
            )
          }}
          onToteDialogClosed={() => {
            window.setTimeout(() => scanInputRef.current?.focus({ preventScroll: true }), 0)
          }}
          onPickRest={line => {
            const current = orders.find(o => o.id === line.id)
            if (current) packUnits(current, Number(current.quantity_ordered))
            scanInputRef.current?.focus()
          }}
        />
      ) : (
      /* Pick list */
      <div className="border rounded-lg p-4 bg-card flex flex-col gap-3">
        <h2 className="text-xl font-semibold text-foreground">Pick List</h2>

        {/* Toolbar (the station has refresh in its top bar) */}
        <div className={cn('flex items-center gap-2 flex-wrap', station && 'hidden')}>
          <div className="relative flex-1 min-w-[160px]">
            <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-8 text-sm"
              placeholder="Filter..."
              value={filterValue}
              onChange={e => setFilterValue(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCSV}
            aria-label="Download CSV"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void getAllOrderLines.trigger(undefined, { skipCache: true })}
            disabled={getAllOrderLines.loading}
            aria-label="Refresh"
          >
            <RefreshCw
              className={cn(
                'w-4 h-4',
                getAllOrderLines.loading && 'animate-spin'
              )}
            />
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(hg => (
                <TableRow key={hg.id}>
                  {hg.headers.map(header => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'whitespace-nowrap',
                        header.column.getCanSort() &&
                          'cursor-pointer select-none'
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() === 'asc'
                        ? ' ↑'
                        : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : ''}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="text-center text-muted-foreground py-8"
                  >
                    {getAllOrderLines.loading
                      ? 'Loading…'
                      : 'No rows found'}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    className={
                      row.original.quantity_remaining === 0
                        ? 'bg-green-50 dark:bg-green-950/30'
                        : ''
                    }
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="whitespace-nowrap">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <p className="text-xs text-muted-foreground">
          {table.getFilteredRowModel().rows.length} row(s)
          {currentOrderId && ` · Filtered to order: ${currentOrderId}`}
        </p>
      </div>
      )}
    </div>
  )
}
